import React, { useState, useMemo } from 'react';
import { Search, Plus, MapPin, Calendar, Trash2, FileText } from 'lucide-react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { toast } from 'sonner';
import { usePerformances, useDeletePerformance, usePerformance, useAddPayment, useDeletePayment } from '../../api/usePerformances';
import type { Performance, PaginatedResponse, PaymentMethodValue, ExternalPhotographer } from '../../api/types';
import { apiGet } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Drawer } from '../../components/ui/Drawer';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { InfoSection, InfoRow } from '../../components/ui/InfoCard';
import { PerformanceForm } from './PerformanceForm';
import { PerformanceExportPdfDrawer } from '../../components/performances/PerformanceExportPdfDrawer';
import { formatDate, formatCurrency, formatPaymentMethod, parseDecimal } from '../../utils/format';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/authStore';

type SortField = 'title' | 'eventDate' | 'status';

function sortPerformances(list: Performance[], field: SortField, order: 'asc' | 'desc') {
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'title': cmp = a.title.localeCompare(b.title); break;
      case 'eventDate': cmp = new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime(); break;
      case 'status': cmp = a.status.localeCompare(b.status); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

const STATUS_LABELS: Record<string, string> = {
  CONFERMATO: 'Confermato',
  COMPLETATO: 'Completato',
  ANNULLATO: 'Annullato',
};

const PAYMENT_METHODS: { value: PaymentMethodValue; label: string }[] = [
  { value: 'CONTANTI', label: 'Contanti' },
  { value: 'BONIFICO', label: 'Bonifico' },
  { value: 'CARTA', label: 'Carta' },
  { value: 'PAYPAL', label: 'PayPal' },
  { value: 'ALTRO', label: 'Altro' },
];

const SELECT_STYLE: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.375rem 0.5rem', outline: 'none',
};

export function PerformancesTab({ scope }: { scope: 'my' | 'all' }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('eventDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editPerformance, setEditPerformance] = useState<Performance | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Payment state
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().split('T')[0]);
  const [payMethod, setPayMethod] = useState<PaymentMethodValue>('CONTANTI');
  const [payNotes, setPayNotes] = useState('');
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);

  const [selectedExternalId, setSelectedExternalId] = useState('');
  const [showExportDrawer, setShowExportDrawer] = useState(false);

  const { data: externalsRaw } = useQuery<ExternalPhotographer[] | { data: ExternalPhotographer[] }>({
    queryKey: ['external-photographers', 'performance-filter'],
    queryFn: () => apiGet('/external-photographers?limit=100&active=true'),
  });
  const externals: ExternalPhotographer[] = Array.isArray(externalsRaw)
    ? externalsRaw
    : (externalsRaw as { data: ExternalPhotographer[] })?.data ?? [];

  const selectedExtName = useMemo(() => {
    if (!selectedExternalId) return undefined;
    const ext = externals.find(e => e.id === selectedExternalId);
    return ext ? (ext.studioName ?? `${ext.firstName} ${ext.lastName}`) : undefined;
  }, [selectedExternalId, externals]);

  const { data, isLoading } = usePerformances({
    status: statusFilter || undefined,
    externalId: selectedExternalId || undefined,
    page,
    limit: 20,
    scope,
  });

  const { data: detail } = usePerformance(selectedId);
  const authUser = useAuthStore(s => s.user);
  const isOwner = detail?.ownerUserId === authUser?.id;
  const deleteMutation = useDeletePerformance();
  const addPaymentMutation = useAddPayment();
  const deletePaymentMutation = useDeletePayment();

  const rawPerformances = data?.data ?? [];
  const performances = useMemo(
    () => sortPerformances(rawPerformances, sortBy, sortOrder),
    [rawPerformances, sortBy, sortOrder]
  );

  const totalPaid = useMemo(
    () => (detail?.payments ?? []).reduce((s, p) => s + parseDecimal(p.amount), 0),
    [detail?.payments]
  );
  const totalAgreed = useMemo(
    () => (detail?.serviceItems ?? []).reduce((s, si) => s + si.agreedPrice, 0),
    [detail?.serviceItems]
  );
  const saldo = totalAgreed - totalPaid;

  const handleSort = (field: SortField) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const columns: ColumnDef<Performance>[] = useMemo(() => {
    const cols: ColumnDef<Performance>[] = [
      { key: 'title', label: 'Prestazione', sortable: true, render: (p) => (
        <div>
          <p style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{p.title}</p>
          {p.location && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
              <MapPin size={10} /> {p.location}
            </p>
          )}
        </div>
      )},
      { key: 'external', label: 'Esterno', render: (p) => (
        <span style={{ fontSize: 'var(--text-sm)' }}>
          {p.externalPhotographer?.studioName ?? `${p.externalPhotographer?.firstName ?? ''} ${p.externalPhotographer?.lastName ?? ''}`}
        </span>
      )},
      ...(scope === 'all' ? [{ key: 'owner', label: 'Owner', render: (p: Performance) => (
        <span style={{ fontSize: 'var(--text-sm)' }}>{p.ownerUser?.name ?? '—'}</span>
      )}] : []),
      { key: 'service', label: 'Servizio', render: (p) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {p.serviceItems?.map(si => si.performanceService?.name).join(' + ') ?? '—'}
        </span>
      )},
      { key: 'eventDate', label: 'Data', sortable: true, render: (p) => (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Calendar size={11} /> {formatDate(p.eventDate)}
        </span>
      )},
      { key: 'status', label: 'Stato', sortable: true, render: (p) => (
        <Badge variant={p.status === 'CONFERMATO' ? 'primary' : p.status === 'COMPLETATO' ? 'success' : 'muted'}>
          {STATUS_LABELS[p.status] ?? p.status}
        </Badge>
      )},
      { key: 'actions', label: '', render: (p) => (
        <button
          onClick={e => { e.stopPropagation(); setDeleteTargetId(p.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem' }}
        >
          <Trash2 size={14} />
        </button>
      )},
    ];
    return cols;
  }, [scope]);

  const handleDelete = () => {
    if (!deleteTargetId) return;
    deleteMutation.mutate(deleteTargetId, {
      onSuccess: () => {
        toast.success('Prestazione eliminata');
        setDeleteTargetId(null);
        if (selectedId === deleteTargetId) setSelectedId(null);
        qc.invalidateQueries({ queryKey: ['performances'] });
      },
      onError: (e: Error) => { toast.error(e.message); setDeleteTargetId(null); },
    });
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditPerformance(null);
  };

  const paymentColumns: ColumnDef<any>[] = [
    { key: 'date', label: 'Data', render: (pay) => <span style={{ color: 'var(--color-text-muted)' }}>{formatDate(pay.paidAt)}</span> },
    { key: 'amount', label: 'Importo', type: 'currency', render: (pay) => <span style={{ fontWeight: 600 }}>{formatCurrency(parseDecimal(pay.amount))}</span> },
    { key: 'method', label: 'Metodo', render: (pay) => <span style={{ color: 'var(--color-text-muted)' }}>{formatPaymentMethod(pay.method)}</span> },
    { key: 'notes', label: 'Note', render: (pay) => <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pay.notes ?? '—'}</span> },
    { key: 'actions', label: '', render: (pay) => (
      <button
        onClick={(e) => { e.stopPropagation(); setDeletePaymentId(pay.id); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: 0, lineHeight: 1 }}
      >
        <Trash2 size={13} />
      </button>
    )},
  ];

  const resetPaymentForm = () => {
    setPayAmount('');
    setPayDate(new Date().toISOString().split('T')[0]);
    setPayMethod('CONTANTI');
    setPayNotes('');
    setShowAddPayment(false);
  };

  const handleAddPayment = () => {
    if (!selectedId || !payAmount || !payDate) { toast.error('Importo e data obbligatori'); return; }
    const amount = parseFloat(payAmount);
    if (isNaN(amount) || amount <= 0) { toast.error('Importo non valido'); return; }
    if (new Date(payDate) > new Date()) {
      toast.error('La data di pagamento non può essere futura');
      return;
    }
    if (totalAgreed > 0 && amount > saldo) {
      toast.error(`L'acconto (${formatCurrency(amount)}) supera il saldo residuo (${formatCurrency(saldo)})`);
      return;
    }
    addPaymentMutation.mutate(
      { perfId: selectedId, amount, paidAt: payDate, method: payMethod, notes: payNotes || undefined },
      {
        onSuccess: () => { toast.success('Acconto aggiunto'); resetPaymentForm(); },
        onError: (e: Error) => toast.error(e.message),
      }
    );
  };

  const handleDeletePayment = () => {
    if (!selectedId || !deletePaymentId) return;
    deletePaymentMutation.mutate(
      { perfId: selectedId, paymentId: deletePaymentId },
      {
        onSuccess: () => { toast.success('Acconto eliminato'); setDeletePaymentId(null); },
        onError: (e: Error) => { toast.error(e.message); setDeletePaymentId(null); },
      }
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: 420 }}>
          <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
          <input
            type="search"
            placeholder="Cerca prestazioni…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
              fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              width: '100%', outline: 'none',
            }}
          />
        </div>
        <select
          value={selectedExternalId}
          onChange={e => { setSelectedExternalId(e.target.value); setPage(1); }}
          style={{
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', color: selectedExternalId ? 'var(--color-text)' : 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', minWidth: 180,
          }}
        >
          <option value="">Tutti gli esterni</option>
          {externals.map(ext => (
            <option key={ext.id} value={ext.id}>
              {ext.studioName ?? `${ext.firstName} ${ext.lastName}`}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
            fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none',
          }}
        >
          <option value="">Tutti gli stati</option>
          <option value="CONFERMATO">Confermato</option>
          <option value="COMPLETATO">Completato</option>
          <option value="ANNULLATO">Annullato</option>
        </select>
        <Button variant="secondary" size="sm" icon={<FileText size={16} />} onClick={() => setShowExportDrawer(true)}>
          Esporta PDF
        </Button>
        <Button onClick={() => { setEditPerformance(null); setShowForm(true); }} icon={<Plus size={16} />}>
          Nuova prestazione
        </Button>
      </div>

      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
      ) : (
        <DataTable
          columns={columns}
          data={performances}
          gridColumns={scope === 'all' ? '2fr 1fr 0.8fr 1fr 0.6fr 0.5fr 44px' : '2fr 1fr 1fr 0.6fr 0.5fr 44px'}
          rowKey={(p) => p.id}
          onRowClick={(p) => setSelectedId(p.id)}
          emptyState={
            <EmptyState
              icon={<Search size={32} />}
              title="Nessuna prestazione trovata"
              description="Crea la tua prima prestazione B2B per un fotografo esterno"
              action={<Button onClick={() => setShowForm(true)} size="sm">Nuova prestazione</Button>}
            />
          }
          footer={data && data.meta.pages > 1 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</Button>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Pagina {page} di {data.meta.pages}</span>
              <Button variant="ghost" size="sm" disabled={page >= data.meta.pages} onClick={() => setPage(p => p + 1)}>→</Button>
            </div>
          ) : undefined}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(field) => handleSort(field as SortField)}
        />
      )}

      {/* Detail Drawer */}
      <Drawer
        open={!!selectedId}
        onClose={() => { setSelectedId(null); resetPaymentForm(); setDeletePaymentId(null); }}
        title={detail?.title ?? 'Dettaglio prestazione'}
        width={480}
        footer={isOwner ? (
          <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
            <Button variant="danger" size="sm" onClick={() => { if (detail?.id) setDeleteTargetId(detail.id); }}>
              Elimina
            </Button>
            <Button onClick={() => { if (detail) { setEditPerformance(detail); setShowForm(true); } }}>
              Modifica
            </Button>
          </div>
        ) : undefined}
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {!isOwner && (
              <div style={{ padding: '0.5rem 0.75rem', background: 'var(--color-warning-bg)', borderRadius: 'var(--radius-interactive)', fontSize: 'var(--text-sm)', color: 'var(--color-warning-text)', marginBottom: '0.75rem' }}>
                Sola lettura — performance di {detail.ownerUser?.name ?? 'altro utente'}
              </div>
            )}
            {/* 4 StatCard */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
              <StatCard
                label="Accordato"
                value={formatCurrency(totalAgreed)}
              />
              <StatCard
                label="Listino"
                value={formatCurrency((detail.serviceItems ?? []).reduce((s, si) => s + si.basePrice, 0))}
              />
              <StatCard label="Acconti" value={formatCurrency(totalPaid)} />
              <StatCard
                label="Saldo"
                value={formatCurrency(saldo)}
                valueStyle={{ color: saldo > 0 ? 'var(--color-error)' : 'var(--color-success)' }}
              />
            </div>

            <InfoSection title="Dettagli">
              <InfoRow label="Esterno" value={
                detail.externalPhotographer
                  ? (detail.externalPhotographer.studioName ?? `${detail.externalPhotographer.firstName} ${detail.externalPhotographer.lastName}`)
                  : '—'
              } />
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 120, flexShrink: 0, lineHeight: '1.6' }}>Servizi</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {(detail.serviceItems ?? []).map(si => (
                    <div key={si.id} style={{ fontSize: 'var(--text-sm)', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                      <span style={{ color: 'var(--color-text)' }}>{si.performanceService?.name}</span>
                      <span style={{ color: si.priceOverridden ? 'var(--color-warning, #e67e22)' : 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrency(si.agreedPrice)}
                        {si.priceOverridden && <span style={{ fontSize: 'var(--text-xs)', marginLeft: '0.25rem' }}>(manuale)</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <InfoRow label="Data" value={formatDate(detail.eventDate)} />
              {detail.location && <InfoRow label="Luogo" value={detail.location} />}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 120, flexShrink: 0 }}>Stato</span>
                <Badge variant={detail.status === 'CONFERMATO' ? 'primary' : detail.status === 'COMPLETATO' ? 'success' : 'muted'}>
                  {STATUS_LABELS[detail.status]}
                </Badge>
              </div>
            </InfoSection>

            {detail.assignments && detail.assignments.length > 0 && (
              <InfoSection title={`Assegnazioni (${detail.assignments.length})`}>
                {detail.assignments.map(a => (
                  <div key={a.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <p style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>
                      {a.collaborator?.name ?? a.user?.name ?? 'N/D'} — {a.role}
                    </p>
                    {a.fee != null && (
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                        Compenso: {formatCurrency(a.fee)}
                      </p>
                    )}
                  </div>
                ))}
              </InfoSection>
            )}

            {/* Acconti */}
            <InfoSection title="Acconti">
              {(detail.payments ?? []).length > 0 ? (
                <DataTable
                  columns={paymentColumns}
                  data={detail.payments ?? []}
                  gridColumns="0.8fr 0.7fr 0.7fr 1fr 28px"
                  rowKey={(pay) => pay.id}
                />
              ) : (
                !showAddPayment && (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)' }}>Nessun acconto registrato</p>
                )
              )}

              {/* Riga aggiunta inline */}
              {showAddPayment && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  padding: '0.75rem', marginTop: '0.5rem',
                  background: 'var(--glass-bg-card)', border: '1px solid var(--color-primary)',
                  borderRadius: 'var(--radius-card)',
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <input
                      type="date"
                      value={payDate}
                      onChange={e => setPayDate(e.target.value)}
                      style={{ ...SELECT_STYLE, boxSizing: 'border-box', width: '100%' }}
                    />
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Importo €"
                      value={payAmount}
                      onChange={e => setPayAmount(e.target.value)}
                      style={{ ...SELECT_STYLE, boxSizing: 'border-box', width: '100%' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <select value={payMethod} onChange={e => setPayMethod(e.target.value as PaymentMethodValue)} style={{ ...SELECT_STYLE, width: '100%', boxSizing: 'border-box' }}>
                      {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <input
                      type="text"
                      placeholder="Note…"
                      value={payNotes}
                      onChange={e => setPayNotes(e.target.value)}
                      style={{ ...SELECT_STYLE, boxSizing: 'border-box', width: '100%' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
                    <Button type="button" variant="ghost" size="sm" onClick={resetPaymentForm}>✕ Annulla</Button>
                    <Button type="button" size="sm" loading={addPaymentMutation.isPending} onClick={handleAddPayment}>✓ Salva</Button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                {!showAddPayment && (
                  <Button type="button" variant="secondary" size="sm" onClick={() => setShowAddPayment(true)}>
                    + Acconto
                  </Button>
                )}
                {(detail.payments ?? []).length > 0 && (
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)', marginLeft: 'auto' }}>
                    Totale: {formatCurrency(totalPaid)}
                  </span>
                )}
              </div>
            </InfoSection>

            {detail.notes && (
              <InfoSection title="Note">
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                  {detail.notes}
                </p>
              </InfoSection>
            )}
          </div>
        )}
      </Drawer>

      {/* Create/Edit Modal */}
      <Modal
        open={showForm}
        onClose={handleFormClose}
        title={editPerformance ? 'Modifica prestazione' : 'Nuova prestazione'}
        footer={null}
      >
        <PerformanceForm
          edit={editPerformance}
          onSuccess={() => { handleFormClose(); qc.invalidateQueries({ queryKey: ['performances'] }); }}
          onCancel={handleFormClose}
        />
      </Modal>

      {/* Delete performance confirm */}
      <ConfirmDialog
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDelete}
        title="Elimina prestazione"
        description="La prestazione verrà eliminata definitivamente."
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
      />

      {/* Delete payment confirm */}
      <ConfirmDialog
        open={!!deletePaymentId}
        onClose={() => setDeletePaymentId(null)}
        onConfirm={handleDeletePayment}
        title="Elimina acconto"
        description="L'acconto verrà eliminato definitivamente."
        confirmLabel="Elimina"
        loading={deletePaymentMutation.isPending}
      />

      <PerformanceExportPdfDrawer
        open={showExportDrawer}
        onClose={() => setShowExportDrawer(false)}
        count={rawPerformances.length}
        statusFilter={statusFilter}
        externalPhotographerId={selectedExternalId || undefined}
        externalPhotographerName={selectedExtName}
      />
    </div>
  );
}

function StatCard({ label, value, valueStyle }: { label: string; value: string; valueStyle?: React.CSSProperties }) {
  return (
    <div style={{
      background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)', padding: '0.75rem',
    }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>{label}</p>
      <p style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-text)', ...valueStyle }}>{value}</p>
    </div>
  );
}
